import React, { useEffect, useState } from 'react';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { C } from '../../constants';
import { CalendarIcon, CheckCircleIcon, ClockIcon, AlertIcon, UserIcon } from '../components/AdminIcons';
import { isToday, isPast, isFuture, parseISO } from '../utils/dateUtils';

function StatCard({ icon, label, value, color, sub }) {
  return (
    <div style={{ background: 'white', borderRadius: '8px', padding: '24px', border: '1px solid rgba(26,68,128,0.08)', display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
      <div style={{ width: '44px', height: '44px', borderRadius: '8px', background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', color, flexShrink: 0 }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: '0.72rem', color: '#8899AA', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '4px' }}>{label}</div>
        <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '2rem', fontWeight: 700, color: '#0B1D3A', lineHeight: 1 }}>{value}</div>
        {sub && <div style={{ fontSize: '0.75rem', color: '#8899AA', marginTop: '4px' }}>{sub}</div>}
      </div>
    </div>
  );
}

export default function DashboardHome({ setPage }) {
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    const q = query(collection(db, 'bookings'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, snap => {
      setBookings(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, []);

  const today = bookings.filter(b => b.date && isToday(b.date));
  const upcoming = bookings.filter(b => b.date && isFuture(b.date) && b.status !== 'completed' && b.status !== 'cancelled');
  const overdue = bookings.filter(b => b.date && isPast(b.date) && b.status === 'pending');
  const completed = bookings.filter(b => b.status === 'completed');

  const recentBookings = [...bookings].slice(0, 5);

  const statusColor = (s) => {
    if (s === 'completed') return { bg: 'rgba(16,185,129,0.1)', color: '#059669' };
    if (s === 'cancelled') return { bg: 'rgba(220,38,38,0.1)', color: '#DC2626' };
    if (s === 'pending') return { bg: 'rgba(245,158,11,0.1)', color: '#D97706' };
    return { bg: 'rgba(26,68,128,0.08)', color: '#1A4480' };
  };

  return (
    <div className="admin-page" style={{ padding: "32px", fontFamily: 'Jost, sans-serif' }}>
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '2rem', fontWeight: 700, color: '#0B1D3A', marginBottom: '4px' }}>Dashboard</h1>
        <p style={{ color: '#8899AA', fontSize: '0.88rem' }}>Welcome back, Christian. Here's what's happening today.</p>
      </div>

      {/* Overdue alert */}
      {overdue.length > 0 && (
        <div style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)', borderRadius: '8px', padding: '14px 18px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ color: '#DC2626' }}><AlertIcon s={20} /></div>
          <div>
            <span style={{ fontWeight: 600, color: '#DC2626', fontSize: '0.88rem' }}>{overdue.length} overdue appointment{overdue.length > 1 ? 's' : ''}</span>
            <span style={{ color: '#8899AA', fontSize: '0.84rem' }}> — These appointments have passed without being marked complete or cancelled.</span>
          </div>
          <button onClick={() => setPage('appointments')} style={{ marginLeft: 'auto', background: 'none', border: '1px solid rgba(220,38,38,0.3)', color: '#DC2626', padding: '6px 14px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', fontFamily: 'Jost, sans-serif', whiteSpace: 'nowrap' }}>View All</button>
        </div>
      )}

      {/* Stat cards */}
      <div className="admin-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '32px' }}>
        <StatCard icon={<CalendarIcon s={20} />} label="Today's Appointments" value={today.length} color="#1A4480" sub="Scheduled for today" />
        <StatCard icon={<ClockIcon s={20} />} label="Upcoming" value={upcoming.length} color="#C8A96E" sub="Not yet attended" />
        <StatCard icon={<CheckCircleIcon s={20} />} label="Completed" value={completed.length} color="#059669" sub="All time" />
        <StatCard icon={<AlertIcon s={20} />} label="Overdue" value={overdue.length} color="#DC2626" sub="Needs attention" />
      </div>

      {/* Today's schedule + Recent bookings */}
      <div className="admin-cards-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Today */}
        <div style={{ background: 'white', borderRadius: '8px', padding: '24px', border: '1px solid rgba(26,68,128,0.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.2rem', fontWeight: 600, color: '#0B1D3A' }}>Today's Schedule</h3>
            <span style={{ fontSize: '0.75rem', color: '#8899AA' }}>{new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
          </div>
          {today.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#8899AA', fontSize: '0.88rem' }}>No appointments scheduled for today.</div>
          ) : (
            today.map(b => {
              const sc = statusColor(b.status || 'pending');
              return (
                <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 0', borderBottom: '1px solid rgba(26,68,128,0.06)' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#F4F7FB', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1A4480', flexShrink: 0 }}><UserIcon s={16} /></div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: '#0B1D3A', fontSize: '0.88rem' }}>{b.name}</div>
                    <div style={{ color: '#8899AA', fontSize: '0.78rem' }}>{b.service}</div>
                  </div>
                  <span style={{ fontSize: '0.72rem', padding: '3px 10px', borderRadius: '20px', background: sc.bg, color: sc.color, fontWeight: 500, textTransform: 'capitalize' }}>{b.status || 'pending'}</span>
                </div>
              );
            })
          )}
        </div>

        {/* Recent bookings */}
        <div style={{ background: 'white', borderRadius: '8px', padding: '24px', border: '1px solid rgba(26,68,128,0.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.2rem', fontWeight: 600, color: '#0B1D3A' }}>Recent Bookings</h3>
            <button onClick={() => setPage('appointments')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.gold, fontSize: '0.78rem', fontFamily: 'Jost, sans-serif' }}>View all →</button>
          </div>
          {recentBookings.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#8899AA', fontSize: '0.88rem' }}>No bookings yet.</div>
          ) : (
            recentBookings.map(b => {
              const sc = statusColor(b.status || 'pending');
              return (
                <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '10px 0', borderBottom: '1px solid rgba(26,68,128,0.06)' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: '#0B1D3A', fontSize: '0.86rem' }}>{b.name}</div>
                    <div style={{ color: '#8899AA', fontSize: '0.76rem' }}>{b.service} · {b.date || 'No date'}</div>
                  </div>
                  <span style={{ fontSize: '0.7rem', padding: '3px 10px', borderRadius: '20px', background: sc.bg, color: sc.color, fontWeight: 500, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{b.status || 'pending'}</span>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
